import { useEffect, useMemo, useState, Fragment } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, Save, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Permissions matrix — every menu key from the backend maps to exactly one
 * page in the sidebar. To keep the screen readable, the 25+ keys are grouped
 * into 8 logical sections that collapse by default. Each group offers a
 * per-role "Toggle all" master checkbox so grants like "all of Purchasing"
 * take a single click instead of nine.
 */
const MENU_GROUPS = [
  { id: "dashboard", label: "Dashboard & My Work", keys: ["dashboard", "my-assignments"] },
  { id: "sales", label: "Sales", keys: ["leads", "calendar", "sales-visits", "surveys", "proposals", "system-templates"] },
  { id: "projects", label: "Projects & Delivery", keys: ["projects", "operations", "work-scheduler", "delivery-checklists", "documentation-tracking"] },
  { id: "finance", label: "Finance & Stock", keys: ["finance", "stock"] },
  { id: "purchasing", label: "Purchasing", keys: ["purchasing", "vendors", "products", "brands", "price-enquiries", "purchase-orders", "grn", "price-history", "purchasing-reports"] },
  { id: "service", label: "Customer Service", keys: ["amc", "tickets", "whatsapp"] },
  { id: "admin", label: "Admin", keys: ["users"] },
  { id: "advanced", label: "Advanced", keys: ["proposal-settings", "stock-adjust", "project-profitability"] },
];

const MENU_LABELS = {
  "dashboard": "Dashboard",
  "my-assignments": "My Assignments",
  "leads": "Leads & CRM",
  "calendar": "Calendar",
  "sales-visits": "Sales Site Visits",
  "surveys": "Site Surveys",
  "proposals": "Proposals",
  "system-templates": "System Templates",
  "projects": "Projects",
  "operations": "Operations",
  "work-scheduler": "Work Scheduler",
  "delivery-checklists": "Delivery Checklists",
  "documentation-tracking": "Documentation Tracking",
  "finance": "Finance",
  "stock": "Stock (Current / Ledger / Low / Reports)",
  "purchasing": "Purchasing Dashboard",
  "vendors": "Vendors",
  "products": "Products",
  "brands": "Brands",
  "price-enquiries": "Price Enquiries",
  "purchase-orders": "Purchase Orders",
  "grn": "Goods Receipt (GRN)",
  "price-history": "Vendor Price History",
  "purchasing-reports": "Purchasing Reports",
  "amc": "AMC",
  "tickets": "Service Tickets",
  "whatsapp": "WhatsApp Log",
  "users": "Users",
  "proposal-settings": "Manage Proposal Setting Presets",
  "stock-adjust": "Manual Stock Adjustment (Stock Ledger)",
  "project-profitability": "Project Profitability View (Project Detail)",
};

export default function Permissions() {
  const [matrix, setMatrix] = useState(null);
  const [busyRole, setBusyRole] = useState(null);
  // Only expand the first group by default so the page opens compact.
  const [openGroups, setOpenGroups] = useState(() => ({ dashboard: true }));

  const load = () => api.get("/role-permissions").then((r) => {
    const map = {};
    r.data.roles.forEach((row) => { map[row.role] = new Set(row.visible_menus); });
    setMatrix({ all_menus: r.data.all_menus, roles: r.data.roles.map((r) => r.role), perms: map });
  });
  useEffect(() => { load(); }, []);

  // Only surface groups whose keys exist in the backend `all_menus` list — this
  // is future-proof against menu keys being added / removed on the server.
  const visibleGroups = useMemo(() => {
    if (!matrix) return [];
    return MENU_GROUPS.map((g) => ({ ...g, keys: g.keys.filter((k) => matrix.all_menus.includes(k)) }))
      .filter((g) => g.keys.length > 0);
  }, [matrix]);

  if (!matrix) return <div className="p-8 label-eyebrow">Loading…</div>;

  const toggle = (role, menu) => {
    if (role === "admin") return; // admin always has all access
    setMatrix((m) => {
      const next = new Set(m.perms[role]);
      next.has(menu) ? next.delete(menu) : next.add(menu);
      return { ...m, perms: { ...m.perms, [role]: next } };
    });
  };

  // Group-level toggle: if every key in the group is currently granted for
  // this role, revoke them all; otherwise grant them all.
  const toggleGroup = (role, group) => {
    if (role === "admin") return;
    setMatrix((m) => {
      const next = new Set(m.perms[role]);
      const allOn = group.keys.every((k) => next.has(k));
      group.keys.forEach((k) => { allOn ? next.delete(k) : next.add(k); });
      return { ...m, perms: { ...m.perms, [role]: next } };
    });
  };

  const groupState = (role, group) => {
    const perms = matrix.perms[role] || new Set();
    const on = group.keys.filter((k) => perms.has(k)).length;
    if (on === 0) return "off";
    if (on === group.keys.length) return "on";
    return "partial";
  };

  const saveRole = async (role) => {
    setBusyRole(role);
    try {
      const visible = Array.from(matrix.perms[role] || []);
      await api.patch("/role-permissions", { role, visible_menus: visible });
      toast.success(`Saved permissions for "${role}"`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusyRole(null); }
  };

  const colMin = 260;
  const roleColMin = 130;
  return (
    <div data-testid="permissions-page">
      <PageHeader
        eyebrow="Access Control"
        title="Role Permissions"
        subtitle="Tick a page and the role can enter it. Groups collapse by default — use the group's row to grant an entire section in one click. Admins always retain full access."
      />

      <div className="px-4 sm:px-8 py-6">
        <div className="border border-[#E5E7EB] overflow-x-auto bg-white">
          <table className="text-sm" style={{ minWidth: `${colMin + matrix.roles.length * roleColMin}px` }}>
            <thead className="bg-[#0a0a0a] text-white">
              <tr>
                <th className="text-left label-eyebrow !text-[10px] !text-white px-4 py-3 sticky left-0 bg-[#0a0a0a] z-10" style={{ minWidth: colMin }}>Page</th>
                {matrix.roles.map((r) => (
                  <th key={r} className="text-center label-eyebrow !text-[10px] !text-white px-4 py-3 capitalize" style={{ minWidth: roleColMin }}>{r}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map((group) => {
                const open = !!openGroups[group.id];
                return (
                  <Fragment key={group.id}>
                    {/* Group header — click to expand/collapse; per-role
                        master checkbox reflects on/off/partial state. */}
                    <tr className="bg-[#F9FAFB] border-y border-[#E5E7EB]" data-testid={`perm-group-${group.id}`}>
                      <td
                        onClick={() => setOpenGroups({ ...openGroups, [group.id]: !open })}
                        className="px-4 py-3 font-black tracking-tight sticky left-0 bg-[#F9FAFB] z-10 cursor-pointer select-none"
                        style={{ minWidth: colMin }}
                      >
                        <div className="flex items-center gap-2">
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span>{group.label}</span>
                          <span className="text-[10px] uppercase tracking-widest font-bold text-[#9CA3AF]">· {group.keys.length}</span>
                        </div>
                      </td>
                      {matrix.roles.map((r) => {
                        const state = groupState(r, group);
                        const locked = r === "admin";
                        return (
                          <td key={r} className="text-center px-4 py-3" style={{ minWidth: roleColMin }}>
                            <input
                              type="checkbox"
                              checked={locked || state === "on"}
                              ref={(el) => { if (el) el.indeterminate = !locked && state === "partial"; }}
                              disabled={locked}
                              onChange={() => toggleGroup(r, group)}
                              className="w-4 h-4 accent-[#0a0a0a] cursor-pointer disabled:opacity-50"
                              data-testid={`perm-group-toggle-${group.id}-${r}`}
                              title="Toggle all pages in this group"
                            />
                          </td>
                        );
                      })}
                    </tr>
                    {/* Per-page rows within the group (only when expanded). */}
                    {open && group.keys.map((m) => (
                      <tr key={m} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`perm-row-${m}`}>
                        <td className="px-4 py-2 sticky left-0 bg-white z-10" style={{ minWidth: colMin }}>
                          <span className="pl-6 text-[13px]">{MENU_LABELS[m] || m}</span>
                        </td>
                        {matrix.roles.map((r) => {
                          const checked = matrix.perms[r]?.has(m);
                          const locked = r === "admin";
                          return (
                            <td key={r} className="text-center px-4 py-2" style={{ minWidth: roleColMin }}>
                              <input
                                type="checkbox"
                                checked={locked ? true : !!checked}
                                disabled={locked}
                                onChange={() => toggle(r, m)}
                                className="w-4 h-4 accent-[#10B981] cursor-pointer disabled:opacity-50"
                                data-testid={`perm-${r}-${m}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
              {/* Save row */}
              <tr className="bg-[#F9FAFB] border-t-2 border-[#0a0a0a]">
                <td className="px-4 py-3 font-bold sticky left-0 bg-[#F9FAFB] z-10" style={{ minWidth: colMin }}></td>
                {matrix.roles.map((r) => (
                  <td key={r} className="text-center px-4 py-3" style={{ minWidth: roleColMin }}>
                    {r === "admin" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-[#9CA3AF] uppercase font-bold tracking-widest" data-testid={`perm-locked-${r}`}>
                        <Lock className="w-3 h-3" /> All Access
                      </span>
                    ) : (
                      <Button
                        onClick={() => saveRole(r)}
                        disabled={busyRole === r}
                        size="sm"
                        className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-[10px] font-bold h-8"
                        data-testid={`perm-save-${r}`}
                      >
                        <Save className="w-3 h-3 mr-1" /> {busyRole === r ? "Saving…" : "Save"}
                      </Button>
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-xs text-[#4B5563]">
          <p>Tip: click a group name to expand it, or use the group&apos;s own checkbox to grant / revoke every page in that section at once. Ask affected users to refresh — the sidebar re-reads permissions from <code className="px-1 bg-[#F3F4F6]">/api/me/menus</code>.</p>
        </div>
      </div>
    </div>
  );
}
